import type {
  ClientFeatureFlag,
  CustomFieldValue,
  PutApiV1ThreeLetterCodePartnerAdminsClientsClientIdBody,
} from '@fukuri-admins/apis/client/gen/model';
import type { CustomFieldDefinition } from '~/types/resources/CustomFieldDefinition';
import type { EditClientFormFieldValues } from '../schema';

type Args = {
  formValues: EditClientFormFieldValues;
  allFeatureFlags: ClientFeatureFlag[];
  customFieldDefinitions: CustomFieldDefinition[];
};

type IGenerateRequestBody = (args: Args) => PutApiV1ThreeLetterCodePartnerAdminsClientsClientIdBody;

/**
 * 単一選択グループID（文字列 | undefined）を number[] に変換する
 * - 未選択（undefined / 空文字）-> []
 * - 数値文字列 -> [数値]
 */
const parseOptionalNumberList = (val: string | undefined): number[] => {
  if (!val) return [];
  const parsed = Number.parseInt(val, 10);
  return Number.isNaN(parsed) ? [] : [parsed];
};

/**
 * 選択済みフィーチャーフラグを { feature_key: boolean } のレコードに変換する
 * - selectedFlags に含まれるキーは true、含まれないキーは false となる
 */
const convertToClientFeatureFlagsRecord = (
  selectedFlags: string[],
  allFlags: ClientFeatureFlag[]
): Record<string, boolean> =>
  allFlags.reduce(
    (acc, flag) => {
      acc[flag.feature_key] = selectedFlags.includes(flag.feature_key);
      return acc;
    },
    {} as Record<string, boolean>
  );

/**
 * customFields レコードを API 送信用の CustomFieldValue 配列に変換する
 *
 * NOTE: `~/utils/customFieldUtils` の `formatCustomFieldValuesForApi` とは戻り値が異なる。
 * API の CustomFieldValue.value は `string | number | boolean` を受け取るため、
 * 配列値は join(',') でカンマ区切り文字列に変換して送信する。
 *
 * `CustomFieldForm`（Select/Checkbox）は選択状態を option の**キー**で管理しているため、
 * フォーム値には option のキーが入っている。choice/multi_choice の場合は
 * `customFieldDefinitions` から対応する定義を引き、`options[key]` の文字列値に変換してから送信する。
 * 対応する定義・option が見つからない場合（データ不整合等）はフォールバックとして元のキーをそのまま送信する。
 */
const convertToCustomFieldValuesPayload = (
  customFields: Record<string, string | string[]> | undefined,
  customFieldDefinitions: CustomFieldDefinition[]
): CustomFieldValue[] =>
  Object.entries(customFields ?? {}).map(([label, value]) => {
    const definition = customFieldDefinitions.find(def => def.label === label);

    if (definition?.input_type === 'choice') {
      const key = Array.isArray(value) ? value[0] : value;
      return { label, value: definition.options?.[key] ?? key };
    }

    if (definition?.input_type === 'multi_choice') {
      const keys = Array.isArray(value) ? value : [value];
      return { label, value: keys.map(key => definition.options?.[key] ?? key).join(',') };
    }

    return { label, value: Array.isArray(value) ? value.join(',') : value };
  });

/**
 * フォーム値からAPIリクエストボディを生成する（新版）
 *
 * - prefectureCode（文字列）は Number() で prefecture_id に変換
 * - partnerBranchId が空文字の場合は null として送信
 * - contactPreferredTime が空文字の場合は null として送信
 * - groupId（単一選択文字列 | undefined）は parseOptionalNumberList で number[] に変換
 * - corporate_number: Zod が `.optional()` で未入力時は空文字が来る。
 *   API に空文字を送ると pattern（13桁）バリデーション違反になるため `|| undefined` で省略する
 *   （CreateClientForm/logics/generateRequestBody と同じ対応）
 *
 * @param formValues - フォームの入力値
 * @param allFeatureFlags - 利用可能なクライアント機能フラグ一覧
 * @param customFieldDefinitions - カスタムフィールド定義一覧（choice/multi_choice のキー→文字列変換に使用）
 */
export const generateRequestBody: IGenerateRequestBody = ({
  formValues,
  allFeatureFlags,
  customFieldDefinitions,
}) => {
  return {
    client: {
      name: formValues.name,
      name_kana: formValues.nameKana,
      representative_name: formValues.representativeName,
      corporate_number: formValues.corporateNumber || undefined,
      external_client_id: formValues.externalClientId,
      employee_count: formValues.employeeCount,
      contact_person_name: formValues.contactPersonName,
      contact_person_name_kana: formValues.contactPersonNameKana,
      contact_person_email: formValues.contactPersonEmail,
      contact_person_phone: formValues.contactPersonPhone,
      contact_preferred_time:
        formValues.contactPreferredTime !== '' ? formValues.contactPreferredTime : null,
      postal_code: formValues.postalCode,
      prefecture_id: formValues.prefectureCode ? Number(formValues.prefectureCode) : null,
      city: formValues.city,
      street_address: formValues.streetAddress,
      billing_contact_name: formValues.billingContactName,
      billing_contact_name_kana: formValues.billingContactNameKana,
      billing_email: formValues.billingEmail,
      partner_branch_id:
        formValues.partnerBranchId !== '' ? Number(formValues.partnerBranchId) : null,
      group_ids: parseOptionalNumberList(formValues.groupId),
      // UI から契約プラン項目を削除した暫定対応。
      // API スキーマ上 gift_plan_id は number | null の必須フィールドのため、常に null を送信する。
      // API 側で gift_plan_id が optional 化 or 廃止されたら、本実装も削除すること。
      gift_plan_id: null,
      client_feature_flags:
        formValues.clientFeatureFlags !== undefined && allFeatureFlags.length > 0
          ? convertToClientFeatureFlagsRecord(formValues.clientFeatureFlags, allFeatureFlags)
          : undefined,
      custom_field_values: convertToCustomFieldValuesPayload(
        formValues.customFields,
        customFieldDefinitions
      ),
    },
  };
};
